// src/components/Instructors.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import PageHeader from "./ui/PageHeader";
import Button from "./ui/Button";
import Modal from "./ui/Modal";
import { TextField } from "./ui/Field";
import { TableSkeleton } from "./ui/Skeleton";

function Instructors() {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentInstructor, setCurrentInstructor] = useState({ name: "" });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchInstructors();
  }, []);

  const fetchInstructors = async () => {
    try {
      setLoading(true);
      const instructorsCollection = collection(db, "instructors");
      const instructorsSnapshot = await getDocs(instructorsCollection);
      const instructorsList = instructorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInstructors(instructorsList);
    } catch (error) {
      console.error("Error fetching instructors: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEdit = async () => {
    try {
      if (isEditing) {
        const instructorRef = doc(db, "instructors", currentInstructor.id);
        await updateDoc(instructorRef, { name: currentInstructor.name });
      } else {
        await addDoc(collection(db, "instructors"), {
          name: currentInstructor.name,
        });
      }
      setShowModal(false);
      fetchInstructors();
    } catch (error) {
      console.error("Error saving instructor: ", error);
    }
  };

  const openAddModal = () => {
    setCurrentInstructor({ name: "" });
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (instructor) => {
    setCurrentInstructor(instructor);
    setIsEditing(true);
    setShowModal(true);
  };

  const sorted = [...instructors].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", "ja")
  );

  return (
    <div>
      <PageHeader
        title="インストラクター管理"
        subtitle={loading ? undefined : `${instructors.length}名`}
        actions={
          <Button variant="primary" onClick={openAddModal}>
            <i className="ti ti-plus" aria-hidden="true" />
            新規追加
          </Button>
        }
      />

      {loading ? (
        <TableSkeleton rows={5} columns={2} />
      ) : sorted.length === 0 ? (
        <div className="card empty">
          <i className="ti ti-users empty__icon" aria-hidden="true" />
          <p style={{ margin: 0 }}>インストラクターがまだ登録されていません</p>
          <Button variant="primary" size="sm" onClick={openAddModal}>
            <i className="ti ti-plus" aria-hidden="true" />
            最初の1人を追加
          </Button>
        </div>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th>名前</th>
                <th style={{ width: 120 }}>ID</th>
                <th style={{ width: 90 }} className="cell-actions">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((instructor) => (
                <tr key={instructor.id}>
                  <td style={{ fontWeight: 500 }}>{instructor.name}</td>
                  <td>
                    <span className="mono">{instructor.id.slice(0, 8)}</span>
                  </td>
                  <td className="cell-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="編集"
                      title="編集"
                      onClick={() => openEditModal(instructor)}
                    >
                      <i className="ti ti-edit" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? "インストラクターを編集" : "新規インストラクター追加"}
        footer={
          <>
            <Button onClick={() => setShowModal(false)}>キャンセル</Button>
            <Button variant="primary" onClick={handleAddEdit}>
              保存
            </Button>
          </>
        }
      >
        <TextField
          label="名前"
          value={currentInstructor.name}
          onChange={(e) =>
            setCurrentInstructor({ ...currentInstructor, name: e.target.value })
          }
        />
      </Modal>
    </div>
  );
}

export default Instructors;
